import { ListChecks, MessageSquare, Paperclip, Repeat, Sparkles } from 'lucide-react';
import type { TaskCard, TaskLabel, TaskMember, TaskType } from '@shared/types/tasks';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { describeTaskRepeat } from '@shared/task-repeat';
import { AvatarStack, LabelChip, memberName } from './task-ui';
import { MemberAvatar, type AvatarSize } from '@/components/team/MemberAvatar';
import { taskPerson } from './task-people';
import { TypeChip, UpstreamChip } from './task-chips';
import { repeatNextLabel } from './task-repeat-label';
import { TASK_KIND_CHIPS, TASK_SIZE_CHIPS } from './task-types';
import { WaitingFaces } from './WaitingOn';
import { PriorityIcon } from './TaskPriority';
import { hasTaskCardPeople, taskCardFields, type TaskTurn } from './task-card-meta';

/**
 * T-92: the details a Board card shows, in pieces, so the sub-task list on a task's page shows the
 * same ones under each title. TaskCardView lays them out as the card; TaskSubtasks as one wrapping line.
 */

/**
 * Its priority (an icon, SF-T-151) and upstream, then its type, size, kind and labels as chips; null when
 * it has none. `lead={false}`: without the priority and upstream, which the Board card puts ahead of its title.
 */
export function TaskCardChips({
  task,
  labels,
  types,
  hideType,
  lead = true,
  className,
}: {
  task: TaskCard;
  labels: TaskLabel[];
  types?: TaskType[];
  hideType?: boolean;
  lead?: boolean;
  className?: string;
}) {
  const fields = taskCardFields(task, types, hideType);
  const { shownType, size, kind } = fields;
  const priority = lead ? fields.priority : null;
  const upstream = lead ? task.upstream : null;
  const suggested = task.ai_suggested ?? [];
  const taskLabels = task.label_ids.map((id) => labels.find((l) => l.id === id)).filter((l): l is TaskLabel => Boolean(l));
  if (!priority && !shownType && !size && !kind && !taskLabels.length && !upstream) return null;
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {priority ? <PriorityIcon priority={priority} /> : null}
      {upstream ? <UpstreamChip upstream={upstream} /> : null}
      {shownType ? <TypeChip type={shownType} suggested={suggested.includes('type')} /> : null}
      {size ? (
        <Badge size="sm" colour={TASK_SIZE_CHIPS[size].colour} data-hint={suggested.includes('size') ? 'Size suggested by Jev' : undefined}>
          {suggested.includes('size') ? <Sparkles aria-label="Suggested by Jev" /> : null}
          {TASK_SIZE_CHIPS[size].label}
        </Badge>
      ) : null}
      {kind ? (
        <Badge size="sm" colour={TASK_KIND_CHIPS[kind].colour} data-hint={suggested.includes('kind') ? 'Kind suggested by Jev' : undefined}>
          {suggested.includes('kind') ? <Sparkles aria-label="Suggested by Jev" /> : null}
          {TASK_KIND_CHIPS[kind].label}
        </Badge>
      ) : null}
      {taskLabels.map((label) => (
        <LabelChip key={label.id} label={label} />
      ))}
    </div>
  );
}

/**
 * Its sub-tasks' x/y (warning-toned when one waits on me), Repeat, and its message and file counts,
 * as a fragment for the caller's row.
 */
export function TaskCardCounts({ task, turn }: { task: TaskCard; turn: TaskTurn }) {
  return (
    <>
      {/* T-42: how many of its sub-tasks are done. */}
      {task.subtasks ? (
        <span
          className={cn('inline-flex items-center gap-1 tabular-nums', turn.mySubtasks.length && 'font-medium text-warning-strong')}
          data-hint={`${task.subtasks.done} of ${task.subtasks.total} sub-tasks done${turn.mySubtasks.length ? `; ${turn.mySubtasks.length} waiting on you` : ''}`}
          data-testid="task-card-subtasks"
        >
          <ListChecks className="size-3.5" />
          {task.subtasks.done}/{task.subtasks.total}
        </span>
      ) : null}
      {task.repeat ? (
        <span
          className="inline-flex items-center"
          data-hint={`${describeTaskRepeat(task.repeat)}${task.repeat_next_at ? `: next back ${repeatNextLabel(task.repeat_next_at)}` : ''}`}
        >
          <Repeat className="size-3.5" />
        </span>
      ) : null}
      {task.chat_count ? (
        <span className="inline-flex items-center gap-1" data-hint={`${task.chat_count} messages`}>
          <MessageSquare className="size-3.5" />
          {task.chat_count}
        </span>
      ) : null}
      {task.attachment_count ? (
        <span className="inline-flex items-center gap-1" data-hint={`${task.attachment_count} files`}>
          <Paperclip className="size-3.5" />
          {task.attachment_count}
        </span>
      ) : null}
    </>
  );
}

/**
 * Whose turn it is (Your turn, or the faces it waits on) and its assignees. A sub-task waiting on me
 * makes it my turn too (T-92), with the hint naming the sub-tasks; others' waits on sub-tasks don't show.
 */
export function TaskCardPeople({
  task,
  members,
  turn,
  formatRef,
  className,
}: {
  task: TaskCard;
  members: TaskMember[];
  turn: TaskTurn;
  /** Its sub-tasks' refs, for the hint (the master board's are in the card's project). */
  formatRef: (n: number) => string;
  className?: string;
}) {
  if (!hasTaskCardPeople(task, turn)) return null;
  // Only from sub-tasks: say which.
  const hint = !turn.ownTurn && turn.mySubtasks.length ? `Waiting on you in ${turn.mySubtasks.map((s) => formatRef(s.number)).join(', ')}` : undefined;
  return (
    <span className={cn('flex shrink-0 items-center gap-2', className)}>
      <WaitingFaces waiting={task.waiting_on} members={members} yourTurn={turn.yourTurn} hint={hint} aiMachine={task.ai_machine?.name} />
      <AvatarStack userIds={task.assignee_ids} members={members} />
    </span>
  );
}

/**
 * SF-T-176: who the task is for (TaskCard.requested_by_user_id: its maker, else whoever first sent it to
 * the AI builder), as a small face top left of its card and task. Null when that isn't known.
 */
export function TaskRequestor({
  task,
  members,
  size = '2xs',
  className,
}: {
  task: Pick<TaskCard, 'requested_by_user_id'>;
  members: TaskMember[];
  size?: AvatarSize;
  className?: string;
}) {
  if (!task.requested_by_user_id) return null;
  const person = taskPerson(members, task.requested_by_user_id);
  const name = memberName(person);
  return (
    <MemberAvatar
      name={name}
      photourl={person?.photourl}
      size={size}
      hint={`Requested by ${name}`}
      className={cn('inline-flex align-middle', className)}
    />
  );
}
